import { VoyageRoute } from "../models/voyage-routing-model";

const DEFAULT_EARTH_RADIUS = 6371000;

export const GeometryUtils = new class
{
    // Calculate the next waypoint for an executing voyage
    // See https://confluence.maerskdev.net/display/voy/Determining+the+current+waypoint+for+an+active+voyage
    // If the current position is more than maxAllowedDeviation (in meters) from the planned path, bail out with an error
    getNextWaypointIndex(route: VoyageRoute, currentPos: number[], maxAllowedDeviation: number): number
    {
        let ErrInvalidGeometry = "Invalid route geometry";
        const planGeometry = route.route_geometry;

        if (planGeometry.length < 3)
        {
            console.warn(`${ErrInvalidGeometry}`);
            return -1;
        }

        // Skip past any waypoints marked explicitly with isPassed
        let lastIsPassedIdx = -1;
        for (let i = 0; i < planGeometry.length; i++)
        {
            if (planGeometry[i].geometry.type == "Point" && planGeometry[i].properties.isPassed)
            {
                lastIsPassedIdx = i;
            }
        }

        let geometry = planGeometry.slice(lastIsPassedIdx+1, planGeometry.length);

        let minDistance = -1;
        let minDistanceIndex = -1;
        // Find the leg we are currently on by looking at the distance
        for (let i = 0; i < geometry.length; i++)
        {
            if (geometry[i].geometry.type === "LineString")
            {
                let distance: number;
                if (geometry[i].properties.leg_type === "GreatCircle")
                {
                    distance = this._getPointToGCDistance(currentPos, <number[]>geometry[i].geometry.coordinates[0], <number[]>geometry[i].geometry.coordinates[1]);
                } else {
                    distance = this._getPointToRLDistance(currentPos, <[number, number]>geometry[i].geometry.coordinates[0], <[number, number]>geometry[i].geometry.coordinates[1]);
                }

                if (minDistanceIndex === -1 || distance! < minDistance)
                {
                    minDistanceIndex = i;
                    minDistance = distance!;
                }
            }
        }

        if (maxAllowedDeviation > 0 && minDistance > maxAllowedDeviation)
        {
            console.warn(`Too far off route, nearest leg %f meters away (maximum %f allowed). minDistance: ${minDistance}. maxAllowedDeviation: ${maxAllowedDeviation}`);   
            return -1;
        }

        let nextWaypointIndex = minDistanceIndex + 1;
        const sqrt2 = 1.414;
        // Skip waypoints until the distance is larger than sqrt(2)*distance to the current legs path (~45degrees correction of original course)
        for (let i = nextWaypointIndex; i < geometry.length; i++)
        {
            if (geometry[i].geometry.type == "Point")
            {
                if (this._getHaversineDist(currentPos, <[number, number]>geometry[i].geometry.coordinates) > minDistance*sqrt2)
                {
                    nextWaypointIndex = i;
                    break;
                }
            }
        }

        return nextWaypointIndex + lastIsPassedIdx + 1;
    }

    getHaversineDist(p1: number[], p2: number[]): number {
        return this._getHaversineDist(p1, p2);
    }

    getRhumblineDist(p1: number[], p2: number[]): number {
        return this._getRhumblineDist(p1, p2);
    }

    // Returns the distance between two points in meters according to the haversine formula (ie. distance along a great circle path)
    private _getHaversineDist(p1: number[], p2: number[]): number {
        return this._getHaversineDistWithR(p1, p2, DEFAULT_EARTH_RADIUS);
    }

    // Returns the distance between two points according to the haversine formula (ie. distance along a great circle path)
    // Uses the supplied value R for earths radius
    private _getHaversineDistWithR(p1: number[], p2: number[], R: number): number {
        return this._getAngularHaversineDist(p1, p2) * R;
    }

    // Calculate angular distance between points using the haversine formula
    private _getAngularHaversineDist(p1: number[], p2: number[]): number {
        // Convert to radians and get deltas
        const lat1 = this._degreesToRadians(p1[1]);
        const lat2 = this._degreesToRadians(p2[1]);
        const deltaLat = this._degreesToRadians(p2[1] - p1[1]);
        const deltaLon = this._degreesToRadians(p2[0] - p1[0]);

        const a = Math.sin(deltaLat/2)*Math.sin(deltaLat/2) + Math.cos(lat1)*Math.cos(lat2)*Math.sin(deltaLon/2)*Math.sin(deltaLon/2);
        const dist = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

        return dist;
    }

    // Calculate distance in meters between two points along a rhumb line
    private _getRhumblineDist(p1: number[], p2: number[]): number {
        return this._getRhumblineDistWithR(p1, p2, DEFAULT_EARTH_RADIUS);
    }

    // Calculate distance between two points along a rhumb line
    // Uses the supplied value R for earths radius
    // Based on the distance calculation described at https://www.movable-type.co.uk/scripts/latlong.html
    private _getRhumblineDistWithR(p1: number[], p2: number[], R: number): number {
        // convert to radians
        const lat1 = this._degreesToRadians(p1[1]);
        const lat2 = this._degreesToRadians(p2[1]);
        const deltaLat = this._degreesToRadians(p2[1] - p1[1]);
        let deltaLon = this._degreesToRadians(p2[0] - p1[0]);
        const projLatDelta = Math.log(Math.tan(Math.PI/4+lat2/2) / Math.tan(Math.PI/4+lat1/2));

        // E-W course becomes ill-conditioned with 0/0
        let q: number;
        
        if (Math.abs(projLatDelta) > 10e-12) {
            q = deltaLat / projLatDelta;
        } else {
            q = Math.cos(lat1);
        }

        // if deltaLon over 180° take shorter rhumb line across the anti-meridian
        if (Math.abs(deltaLon) > Math.PI) {
            if (deltaLon > 0) {
                deltaLon = -(2*Math.PI - deltaLon);
            } else {
                deltaLon = 2*Math.PI + deltaLon;
            }
        }

        return Math.sqrt(deltaLat*deltaLat+q*q*deltaLon*deltaLon) * R;
    }

    // Get distance from a point to a rhumb line segment in meters
    private _getPointToRLDistance(point: number[], lineStart: number[], lineEnd: number[]): number {
        return this._getPointToRLDistanceWithR(point, lineStart, lineEnd, DEFAULT_EARTH_RADIUS);
    }

    // Get distance from a point to a rhumb line segment
    // Uses the supplied value R for earths radius
    private _getPointToRLDistanceWithR(point: number[], lineStart: number[], lineEnd: number[], R: number): number {
        // Find closest point on the line by converting to mercator projection (where a rhumb line is a straight line)
        // and using simple trigonometri - then use point-to-point distance to get the distance to the line segment
        const mercPoint = this._latLongToMercator(point);
        const mercLineStart = this._latLongToMercator(lineStart);
        const mercLineEnd = this._latLongToMercator(lineEnd);
        const mercLineLength = Math.sqrt(Math.pow(mercLineEnd[0]-mercLineStart[0], 2) + Math.pow(mercLineEnd[1]-mercLineStart[1], 2));

        if (Math.abs(mercLineStart[0]-mercLineEnd[0]) > Math.PI) {
            // Crossing the dateline, adjust the negative part(s) to be positive
            if (mercLineStart[0] < 0) {
                mercLineStart[0] += 2 * Math.PI;
            } else {
                mercLineEnd[0] += 2 * Math.PI;
            }
            if (mercPoint[0] < 0) {
                mercPoint[0] += 2 * Math.PI;
            }
        }

        const angleLine = Math.atan2(mercLineEnd[1]-mercLineStart[1], mercLineEnd[0]-mercLineStart[0]);
        const angleToPoint = Math.atan2(mercPoint[1]-mercLineStart[1], mercPoint[0]-mercLineStart[0]);
        const angle = Math.abs(angleToPoint - angleLine);
        const h = Math.sqrt(Math.pow(mercPoint[0]-mercLineStart[0], 2) + Math.pow(mercPoint[1]-mercLineStart[1], 2));

        const crossPointDistFromStart = h * Math.cos(angle);

        if (crossPointDistFromStart <= 0) {
            // Negative or no distance along the line -> point falls outside segment, use point-to-point distance from start point
            return this._getHaversineDistWithR(point, lineStart, R);
        } else if (crossPointDistFromStart >= mercLineLength) {
            // Distance is greater or equal to the line segment length -> point falls outside segment, use point-to-point distance from end point
            return this._getHaversineDistWithR(point, lineEnd, R);
        }

        const mercCrossPointX = mercLineStart[0] + crossPointDistFromStart*Math.cos(angleLine);
        const mercCrossPointY = mercLineStart[1] + crossPointDistFromStart*Math.sin(angleLine);

        const crossPoint = this._mercatorToLatLong([mercCrossPointX, mercCrossPointY]);

        return this._getHaversineDistWithR(point, crossPoint, R);
    }

    private _radiansToDegrees(radians: number): number {
        return radians * 180 / Math.PI;
    }

    private _degreesToRadians(degrees: number): number {
        return degrees * Math.PI / 180;
    }

    // Convert latitude/longitude coordinates to a mercator projection
    // See formula: https://en.wikipedia.org/wiki/Mercator_projection#Derivation_of_the_Mercator_projection
    // Scale (R) is set to 1 and λ_0 is set to 0
    private _latLongToMercator(point: number[]): number[] {
        const x = this._degreesToRadians(point[0]);

        const y = Math.log(Math.tan((Math.PI / 4) + (this._degreesToRadians(point[1]) / 2)));

        return [x, y];
    }

    // Convert mercator projection coordinates to latitude/longitude
    // See formula: https://en.wikipedia.org/wiki/Mercator_projection#Inverse_transformations
    // Scale (R) is set to 1 and λ_0 is set to 0
    private _mercatorToLatLong(point: number[]): number[] {
        const long = this._radiansToDegreesNormalized(point[0]);

        const lat = this._radiansToDegrees(2*Math.atan(Math.exp(point[1])) - Math.PI/2);

        return [long, lat];
    }

    // Convert from radians and normalize values (ie. make sure the value is in the range -180 -> 180)
    private _radiansToDegreesNormalized(radians: number): number {
        let degrees = this._radiansToDegrees(radians);
        if (degrees < -180) {
            degrees += 360;
        } else if (degrees > 180) {
            degrees -= 360;
        }
        return degrees;
    }

    // Returns the initial heading when going on a great circle from p1 to p2
    // Based on the bearing calculation described at https://www.movable-type.co.uk/scripts/latlong.html
    private _getHeadingGC(p1: number[], p2: number[]): number {
        const lat1 = this._degreesToRadians(p1[1])
        const lat2 = this._degreesToRadians(p2[1])
        const long1 = this._degreesToRadians(p1[0])
        const long2 = this._degreesToRadians(p2[0])
    
        const y = Math.sin(long2-long1) * Math.cos(lat2)
        const x = Math.cos(lat1)*Math.sin(lat2) -
            Math.sin(lat1)*Math.cos(lat2)*Math.cos(long2-long1)
        const angle = Math.atan2(y, x)
        return (angle*180/Math.PI + 360) % 360 // convert to (positive) degrees before returning
    }

    // Get distance from a point to a great circle line segment in meters
    // Uses crossTrackDistanceTo if the point is inside the segment and otherwise uses point-to-point distance
    private _getPointToGCDistance(point: number[], lineStart: number[], lineEnd: number[]): number {
        return this._getPointToGCDistanceWithR(point, lineStart, lineEnd, DEFAULT_EARTH_RADIUS)
    }

    // Get distance from a point to a great circle line segment
    // Uses the supplied value R for earths radius
    // Uses crossTrackDistanceTo if the point is inside the segment and otherwise uses point-to-point distance
    private _getPointToGCDistanceWithR(point: number[], lineStart: number[], lineEnd: number[], R: number): number {
        // First check if we are on the line segment
        const distAlongTrack = this._getAlongTrackDistanceToWithR(point, lineStart, lineEnd, R)
        const lineDist = this._getHaversineDistWithR(lineStart, lineEnd, R)
        if (distAlongTrack <= 0 || distAlongTrack >= lineDist) {
            // Outside the segment (or on the start/end point) - use the start/end point distance
            const startPointDist = this._getHaversineDistWithR(lineStart, point, R)
            const endPointDist = this._getHaversineDistWithR(lineEnd, point, R)
            return Math.min(startPointDist, endPointDist)
        }
        // Inside the segment, use cross track error
        return this._getGCCrossTrackDistanceToWithR(point, lineStart, lineEnd, R)
    }
    
    // Calculates how far along a great circle leg we are given a point (that is not necessarily on the line)
    // the path.
    // Uses the supplied value R for earths radius
    // Based on the alongTrackDistanceTo() function in https://github.com/chrisveness/geodesy
    private _getAlongTrackDistanceToWithR(point: number[], lineStart: number[], lineEnd: number[], R: number): number {
        if (point[0] == lineStart[0] && point[1] == lineStart[1]) {
            return 0
        }
        // Note: Distances and bearings in radians
        const distStartToPos = this._getAngularHaversineDist(lineStart, point)
        const bearingStartToPos = this._degreesToRadians(this._getHeadingGC(lineStart, point))
        const bearingLine = this._degreesToRadians(this._getHeadingGC(lineStart, lineEnd))

        const distToTrack = Math.asin(Math.sin(distStartToPos) * Math.sin(bearingStartToPos-bearingLine))
        const distAlongTrack = Math.acos(Math.cos(distStartToPos) / Math.abs(Math.cos(distToTrack)))

        let sign = 1.0
        if (Math.cos(bearingLine-bearingStartToPos) < 0) {
            sign = -1
        }

        return distAlongTrack * sign * R
    }

    // Calculates the distance from a point to a great circle line in meters
    // Note: Does not handle points being outside the segment - for those a point-to-point distance should be used instead
    // Based on http://www.edwilliams.org/avform.htm#XTE and its implementation in https://github.com/chrisveness/geodesy
    private _getGCCrossTrackDistanceToWithR(point: number[], lineStart: number[], lineEnd: number[], R: number): number {
        if (point[0] == lineStart[0] && point[1] == lineStart[1]) {
            return 0
        }

        // Note: Distances and bearings in radians
        const distStartToPos = this._getAngularHaversineDist(lineStart, point)
        const bearingStartToPos = this._degreesToRadians(this._getHeadingGC(lineStart, point))
        const bearingLine = this._degreesToRadians(this._getHeadingGC(lineStart, lineEnd))

        const dist = Math.asin(Math.sin(distStartToPos) * Math.sin(bearingStartToPos-bearingLine))

        return Math.abs(dist) * R
    }
}
